import React, { useState, useEffect, useRef } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./Hstories.css";

// Premium Loader Component
const PremiumLoader = () => (
  <div className="stories-loader-wrapper">
    <div className="stories-ring-loader">
      <div></div>
      <div></div>
      <div></div>
      <div></div>
    </div>
    <p className="stories-loader-text">Loading Happy stories...</p>
  </div>
);

// Empty State Component
const EmptyState = () => (
  <div className="stories-empty-state">
    <div className="stories-empty-icon">💕</div>
    <h3 className="stories-empty-title">No Stories Yet</h3>
    <p className="stories-empty-text">Share your happy story with us!</p>
  </div>
);

// Shuffle Array Function
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const Hstories = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const sliderRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          "https://tulirmatrimony.com/controlapi/successlist.php"
        );
        const result = await response.json();
        if (result?.body?.length) {
          const shuffledStories = shuffleArray(result.body).slice(0, 8);
          setStories(shuffledStories);
        } else {
          setStories([]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setStories([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const settings = {
    dots: true,
    infinite: stories.length > 1,
    speed: 600,
    slidesToShow: 4,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4500,
    pauseOnHover: true,
    beforeChange: (oldIndex, newIndex) => setActiveIndex(newIndex),
    responsive: [
      {
        breakpoint: 1400,
        settings: {
          slidesToShow: 3,
        }
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
          arrows: false,
        }
      },
      {
        breakpoint: 576,
        settings: {
          slidesToShow: 1,
          arrows: false,
          centerMode: true,
          centerPadding: "20px",
        }
      }
    ]
  };

  if (loading) {
    return <PremiumLoader />;
  }

  if (!stories.length) {
    return <EmptyState />;
  }

  return (
    <section className="stories-premium-section">
      {/* Background Effects */}
      <div className="stories-bg-effects">
        <div className="bg-glow glow-1"></div>
        <div className="bg-glow glow-2"></div>
      </div>

      <div className="stories-container">
        {/* Section Header */}
        <div className="stories-header">
          <div className="header-decoration">
            <span className="decoration-line"></span>
            <div className="floating-hearts-header">
              <span>❤️</span>
              <span>💕</span>
              <span>💖</span>
            </div>
            <span className="decoration-line"></span>
          </div>
          <h2 className="stories-title">
            <span className="gradient-text">Happy</span> Stories
          </h2>
          <p className="stories-subtitle">Celebrating the beautiful journey of our couples</p>
        </div>

        {/* Stories Grid */}
        <div className="stories-slider-wrapper">
          <Slider ref={sliderRef} {...settings}>
            {stories.map((story, index) => (
              <div key={index} className="stories-slide">
                <div className="stories-card">
                  {/* Image Section - Full Portrait */}
                  <div className="card-image-section">
                    {story.image ? (
                      <div className="image-wrapper">
                        <img
                          src={`data:image/jpeg;base64,${story.image}`}
                          alt={`${story.bridename} & ${story.groom_name}`}
                          className="couple-portrait"
                        />
                        <div className="image-gradient-overlay"></div>
                        <div className="image-shine"></div>
                      </div>
                    ) : (
                      <div className="image-placeholder-portrait">
                        <div className="placeholder-content">
                          <span className="placeholder-heart-big">💑</span>
                          <span className="placeholder-text">Love Story</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Floating Badges */}
                    <div className="floating-badge top-badge">
                      <span className="badge-heart">❤️</span>
                      <span>Success Story</span>
                    </div>
                    
                    <div className="floating-badge date-badge">
                      <span className="badge-icon">📅</span>
                      <span>{story.marriage_date || "2024"}</span>
                    </div>
                  </div>

                  {/* Content Section - Overlay Style */}
                  <div className="card-content-section">
                    <div className="content-inner">
                      {/* Couple Names with Ring Icon */}
                      <div className="couple-names-container">
                        <div className="ring-icon">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M12 2C8.13 2 5 5.13 5 9C5 12.87 8.13 16 12 16C15.87 16 19 12.87 19 9C19 5.13 15.87 2 12 2Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                            <path d="M12 22V16M8 22H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            <circle cx="12" cy="9" r="2" stroke="currentColor" strokeWidth="1.5"/>
                          </svg>
                        </div>
                        <div className="couple-names">
                          <span className="bride-name">{story.bridename || "Bride"}</span>
                          <span className="ampersand">✦</span>
                          <span className="groom-name">{story.groom_name || "Groom"}</span>
                        </div>
                      </div>

                      {/* Love Quote */}
                      <div className="love-quote-container">
                        <div className="quote-mark quote-start">"</div>
                        <p className="love-message">
                          {story.Message || "Together forever, happily ever after"}
                        </p>
                        <div className="quote-mark quote-end">"</div>
                      </div>

                      {/* Verification & Rating */}
                      <div className="story-footer">
                        <div className="verification-chip">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                            <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2"/>
                          </svg>
                          <span>Verified</span>
                        </div>
                        <div className="rating-chip">
                          <span className="star">★</span>
                          <span className="star">★</span>
                          <span className="star">★</span>
                          <span className="star">★</span>
                          <span className="star">★</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Hover Effects */}
                  <div className="card-hover-effect"></div>
                </div>
              </div>
            ))}
          </Slider>
        </div>

        {/* Progress Indicator */}
        <div className="stories-progress-indicator">
          <div className="progress-dots">
            {stories.map((_, idx) => (
              <button
                key={idx}
                className={`progress-dot ${activeIndex === idx ? 'active' : ''}`}
                onClick={() => sliderRef.current?.slickGoTo(idx)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hstories;